"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Calendar = void 0;
var date_fns_1 = require("date-fns");
var PropTypes = require("prop-types");
var React = require("react");
var mathUtil_1 = require("../../utils/mathUtil");
var CalendarDay_1 = require("./CalendarDay");
var Calendar = function (_a) {
    var onChangeCalendarStatus = _a.onChangeCalendarStatus, onChangeCalendarMonth = _a.onChangeCalendarMonth, calendarPosition = _a.calendarPosition, _b = _a.className, className = _b === void 0 ? '' : _b, _c = _a.dateFormat, dateFormat = _c === void 0 ? 'DD/MM/YYYY' : _c, displayOnHover = _a.displayOnHover, id = _a.id, ignoreDates = _a.ignoreDates, ignoreWeekends = _a.ignoreWeekends, inFocusChange = _a.inFocusChange, isActive = _a.isActive, month = _a.month, onClick = _a.onClick, year = _a.year, smallScreenSize = _a.smallScreenSize, startDate = _a.startDate, startFocusDate = _a.startFocusDate, endDate = _a.endDate, maximumDate = _a.maximumDate, minimumDate = _a.minimumDate, _d = _a.caption, caption = _d === void 0 ? '' : _d;
    var isoWeek = [
        { display: 'M', screenReader: 'Monday' },
        { display: 'T', screenReader: 'Tuesday' },
        { display: 'W', screenReader: 'Wednesday' },
        { display: 'T', screenReader: 'Thursday' },
        { display: 'F', screenReader: 'Friday' },
        { display: 'S', screenReader: 'Saturday' },
        { display: 'S', screenReader: 'Sunday' },
    ];
    var calendar = (React.useRef());
    var _e = React.useState(startDate && (0, date_fns_1.isValid)(startDate) ? startDate : new Date()), focusDate = _e[0], setFocusDate = _e[1];
    var _f = React.useState(false), dateInFocus = _f[0], setDateInFocus = _f[1];
    var daysInWeek = 7;
    var isFirstDay = function (day) { return startDate && (0, date_fns_1.isSameDay)(startDate, new Date(year, month, day)); };
    var isLastDay = function (day) { return endDate && (0, date_fns_1.isSameDay)(endDate, new Date(year, month, day)); };
    var isSelected = function (day) {
        if (startDate && endDate) {
            try {
                return (0, date_fns_1.isWithinInterval)(new Date(year, month, day), { start: startDate, end: endDate });
            }
            catch (_a) {
                return false;
            }
        }
        return false;
    };
    /**
     * Check to see if the day is selectable.
     * Used when focus is changed and when a day selection is attempted
     */
    var isDaySelectable = function (dateToCompare) {
        var isSelectable = true;
        if (maximumDate) {
            isSelectable = isSelectable && ((0, date_fns_1.isAfter)(maximumDate, dateToCompare) || (0, date_fns_1.isSameDay)(dateToCompare, maximumDate));
        }
        if (minimumDate) {
            isSelectable = isSelectable && ((0, date_fns_1.isBefore)(minimumDate, dateToCompare) || (0, date_fns_1.isSameDay)(dateToCompare, minimumDate));
        }
        if (ignoreWeekends) {
            isSelectable = isSelectable && !(0, date_fns_1.isWeekend)(dateToCompare);
        }
        if (ignoreDates && ignoreDates.length >= 1) {
            isSelectable = isSelectable && !ignoreDates.includes((0, date_fns_1.format)(dateToCompare, dateFormat));
        }
        return isSelectable;
    };
    // tslint:disable-next-line
    var createCalendarMatrix = function () {
        var daysInMonth = (0, date_fns_1.getDaysInMonth)(new Date(year, month));
        var daysNotInMonthFirstWeek = (0, mathUtil_1.mod)((0, date_fns_1.startOfMonth)(new Date(year, month)).getDay() - 1, daysInWeek);
        var daysInMonthLastWeek = (0, mathUtil_1.mod)(daysNotInMonthFirstWeek + daysInMonth, daysInWeek);
        var daysNotInMonthLastWeek = (0, mathUtil_1.mod)(daysInWeek - daysInMonthLastWeek, daysInWeek);
        var calendarDays = __spreadArray(__spreadArray(__spreadArray([], Array(daysNotInMonthFirstWeek).fill(null), true), Array(daysInMonth)
            .fill(undefined)
            .map(function (_, index) { return index + 1; }), true), Array(daysNotInMonthLastWeek).fill(null), true);
        var weeksInMonth = calendarDays.length / daysInWeek;
        var calendarMatrix = Array(weeksInMonth)
            .fill(undefined)
            .map(function (_, index) { return calendarDays.slice(index * daysInWeek, index * daysInWeek + daysInWeek); });
        return calendarMatrix;
    };
    /**
     * on day click event
     * watch the mouse click and select the date if it can be set.
     * update the focus
     * @param event mouse click
     */
    var onDayClick = function (event) {
        if (!event.currentTarget.dataset.day) {
            /* istanbul ignore next */
            return;
        }
        if (!isDaySelectable(new Date(year, month, Number(event.currentTarget.dataset.day)))) {
            return;
        }
        var cTarget = event.currentTarget.dataset;
        setFocusDate(new Date(Number(cTarget.year), Number(cTarget.month), Number(cTarget.day)));
        event.currentTarget.focus();
        onClick(event);
    };
    var isFirstRun = React.useRef(true);
    React.useEffect(function () {
        if (isFirstRun.current) {
            isFirstRun.current = false;
            return;
        }
        if (calendar.current) {
            var activeElement = calendar.current.querySelectorAll('button[tabindex="0"]')[0];
            if (activeElement && dateInFocus) {
                activeElement.focus();
            }
        }
        // eslint-disable-next-line
    }, [focusDate]);
    /**
     * What should be focused manager.
     * Used by DatePicker input field and previous and next buttons,
     */
    React.useEffect(function () {
        switch (inFocusChange) {
            case 'button-click':
            case 'button-click2':
                startFocusDate && isDaySelectable(startFocusDate) ? setFocusDate(startFocusDate) : setFocusDate(new Date(year, month, 1));
                break;
            case 'day-scroll':
            case 'day-scroll2':
                setDateInFocus(true);
                startFocusDate && isDaySelectable(startFocusDate) ? setFocusDate(startFocusDate) : setFocusDate(new Date(year, month, 1));
                break;
            case 'start-date':
            case 'start-date2':
                startDate && isDaySelectable(startDate) ? setFocusDate(startDate) : setFocusDate(new Date(year, month, 1));
                break;
            case 'end-date':
            case 'end-date2':
                endDate && isDaySelectable(endDate) ? setFocusDate(endDate) : setFocusDate(new Date(year, month, 1));
                break;
            case 'start-date-invalid':
                setFocusDate(new Date());
                break;
        }
        // eslint-disable-next-line
    }, [inFocusChange]);
    /**
     * watch to see when calendar is active and set focus then only if the calendar is not inline
     */
    React.useEffect(function () {
        if (displayOnHover) {
            // dealing with 2 calendars
            if (calendarPosition === 1 || calendarPosition === 2) {
                if (calendarPosition === 1 && !startFocusDate) {
                    setDateInFocus(true);
                    startFocusDate ? setFocusDate(startFocusDate) : setFocusDate(new Date(year, month, 1));
                }
                else if (calendarPosition === 2 && (startFocusDate === null || startFocusDate === void 0 ? void 0 : startFocusDate.getMonth()) === month) {
                    setDateInFocus(true);
                    setFocusDate(startFocusDate);
                }
                else if (calendarPosition === 1 && (startFocusDate === null || startFocusDate === void 0 ? void 0 : startFocusDate.getMonth()) === month) {
                    setDateInFocus(true);
                    setFocusDate(startFocusDate);
                }
                else {
                    setDateInFocus(false);
                }
            }
            else {
                setDateInFocus(true);
                startFocusDate ? setFocusDate(startFocusDate) : setFocusDate(new Date(year, month, 1));
            }
        }
        // eslint-disable-next-line
    }, [isActive]);
    /**
     * A function to handle moving focus. This can either be up, down, left or right.
     * The new position is checked to see if the day is selectable and do nothing if that is the case
     * The function also checks if you are changing calendar views doing this move.
     * If moving calendars it checks if this calendar is part of a date range picker or single.
     * If date range picker check if you are small screen
     * Final outcome is update the focus position and fire off the change month handler if this has occured.
     * @param event any
     * @param amount how many places to move
     * @param direction what direction to move in. previous or next
     */
    var updateFocusPosition = function (event, amount, direction) {
        var newFocusDate = new Date((0, date_fns_1.addDays)(focusDate, amount));
        if (!isDaySelectable(newFocusDate)) {
            if (maximumDate && !(0, date_fns_1.isBefore)(newFocusDate, maximumDate)) {
                return;
            }
            if (minimumDate && !(0, date_fns_1.isAfter)(newFocusDate, minimumDate)) {
                return;
            }
            while (!isDaySelectable(newFocusDate)) {
                newFocusDate = new Date((0, date_fns_1.addDays)(newFocusDate, amount));
            }
        }
        if (isDaySelectable(new Date(newFocusDate.getFullYear(), newFocusDate.getMonth(), newFocusDate.getDate()))) {
            if (focusDate.getMonth() !== newFocusDate.getMonth()) {
                if (calendarPosition && smallScreenSize && window.innerWidth > smallScreenSize && onChangeCalendarStatus) {
                    if (calendarPosition === 1 && direction === 'next') {
                        onChangeCalendarStatus(newFocusDate, 2);
                    }
                    else if (calendarPosition === 2 && direction === 'previous') {
                        onChangeCalendarStatus(newFocusDate, 1);
                    }
                    else {
                        onChangeCalendarMonth(direction);
                    }
                }
                else {
                    onChangeCalendarMonth(direction);
                }
            }
            setFocusDate(newFocusDate);
        }
    };
    var downHandler = function (event) {
        switch (event.keyCode) {
            case 39: // arrow right
                event.preventDefault();
                updateFocusPosition(event, 1, 'next');
                break;
            case 37: // arrow left
                event.preventDefault();
                updateFocusPosition(event, -1, 'previous');
                break;
            case 38: // arrow up
                event.preventDefault();
                updateFocusPosition(event, -daysInWeek, 'previous');
                break;
            case 40: // arrow down
                event.preventDefault();
                updateFocusPosition(event, daysInWeek, 'next');
                break;
        }
    };
    var leaveHandler = function (event) {
        setDateInFocus(false);
    };
    var entryHandler = function (event) {
        setDateInFocus(true);
    };
    var attributes = {
        className: "calendar " + className,
        id: id + "-date-picker",
        onBlur: leaveHandler,
        onFocus: entryHandler,
        onKeyDown: downHandler,
        ref: calendar,
        role: 'grid',
    };
    return (React.createElement("table", __assign({}, attributes),
        React.createElement("caption", { className: "sr-only" }, caption),
        React.createElement("thead", null,
            React.createElement("tr", null, isoWeek.map(function (day) { return (React.createElement("th", { scope: "col", key: "calendar-" + id + "__day-" + day.screenReader, className: "calendar__day Text16SecondaryCenter" },
                React.createElement("span", { "aria-hidden": "true" }, day.display),
                React.createElement("span", { className: "sr-only" }, day.screenReader))); }))),
        React.createElement("tbody", { "data-infocus": dateInFocus, "data-focusdate": focusDate }, createCalendarMatrix().map(function (week, weekIndex) {
            return (
            // eslint-disable-next-line react/no-array-index-key
            React.createElement("tr", { key: "calendar-" + id + "__week-" + week + "-index-" + weekIndex }, week.map(function (day, dayIndex) {
                return day ? (React.createElement(React.Fragment, { key: "calendar-" + id + "-day-" + day + "-index-" + weekIndex },
                    React.createElement(CalendarDay_1.CalendarDay, { day: day, month: month, year: year, id: "calendar-" + id + "__week-" + week + "-index-" + weekIndex, inFocus: (inFocusChange === 'inactive') ? false : true, isSelected: isSelected(day), isFirstDay: isFirstDay(day), isLastDay: isLastDay(day), isDaySelectable: isDaySelectable(new Date(year, month, day)), focusDate: focusDate, dateInFocus: dateInFocus, onDayClick: onDayClick }))) : (
                /* eslint-disable react/no-array-index-key */
                React.createElement("td", { key: "calendar-" + id + "__date-day-" + dayIndex + "-week-" + weekIndex + "-date-empty", className: "calendar__date--empty" })
                /* eslint-enable react/no-array-index-key */
                );
            })));
        }))));
};
exports.Calendar = Calendar;
exports.Calendar.defaultProps = {};
exports.Calendar.propTypes = {
    calendarPosition: PropTypes.number,
    caption: PropTypes.string,
    className: PropTypes.string,
    dateFormat: PropTypes.string,
    displayOnHover: PropTypes.bool,
    endDate: PropTypes.instanceOf(Date),
    id: PropTypes.string.isRequired,
    ignoreDates: PropTypes.array,
    ignoreWeekends: PropTypes.bool,
    inFocusChange: PropTypes.string,
    isActive: PropTypes.bool,
    maximumDate: PropTypes.instanceOf(Date),
    minimumDate: PropTypes.instanceOf(Date),
    month: PropTypes.number.isRequired,
    onChangeCalendarMonth: PropTypes.func.isRequired,
    onChangeCalendarStatus: PropTypes.func,
    onClick: PropTypes.func.isRequired,
    smallScreenSize: PropTypes.number,
    startDate: PropTypes.instanceOf(Date),
    startFocusDate: PropTypes.instanceOf(Date),
    year: PropTypes.number.isRequired,
};
exports.default = exports.Calendar;
